import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users } from '../../../models/User.tsx';
import { editUser, showPlayers } from '../../../services/admin/UserService.tsx';
import { FaLock } from "react-icons/fa6";
import { FaSyncAlt, FaUserEdit } from "react-icons/fa";
import { addTransaction } from '../../../services/admin/Transaction.tsx';
import { Timestamp } from 'firebase/firestore';
import { IoArrowRedoSharp } from "react-icons/io5";
import { Modal } from 'bootstrap';
import { logUserActivity } from '../../../services/player/LoginService.tsx';
import { useUser } from '../../../context/UserContext.tsx';
import AgentContext from '../../../context/admin/Agent.tsx';
import { buildVisitedUserNavState, patchVisitedUserCaches } from '../../../helpers/adminVisitedUserCache.tsx';
import { livePinLookup, withLivePins } from '../../../helpers/liveUserPin.ts';
import { formatCurrency, formatNumbers } from '../../../helpers/functions.tsx';
import { invalidatePrefix, useCachedQuery } from '../../../helpers/queryCache.tsx';
import { formatAdminFiguresShortDate } from '../../../helpers/adminFiguresTime.tsx';
import { agentCanAdjustBalances, isBlockedBalanceAction } from '../../../services/player/SportService.tsx';
import { useAgentPermissions } from '../../../helpers/useAgentPermissions.tsx';

const AgentManagement = () => {

    const { user: currentUser, userIpData: userIpDataAdmin } = useUser();
    const { allowCreditDebitAdjustments } = useAgentPermissions(currentUser);
    const balanceActor = currentUser ? { ...currentUser, allowCreditDebitAdjustments } : currentUser;
    const { allAgents, updateAgentInContext, refreshRoster } = useContext(AgentContext);

    const [expandedAgentIds, setExpandedAgentIds] = useState<string[]>([]);
    const [selectedSubAgent, setSelectedSubAgent] = useState<Users | null>(null);
    const [newPassword, setNewPassword] = useState<string>('');
    const [formData, setFormData] = useState({
        createDate: formatAdminFiguresShortDate(),
        agent: '',
        customer: '',
        transactionType: 'deposit',
        description: 'Customer Deposit',
        amount: '',
        createBy: '',
    })

    const cacheKeyParts = ['agentPlayers', currentUser?.userName];
    const { data: agentData, isLoading, refetch } = useCachedQuery(
        cacheKeyParts,
        () => showPlayers(currentUser!),
        [currentUser?.userName],
        { ttlMs: 30_000 }
    );
    const agent = useMemo(
        () => withLivePins(agentData ?? [], livePinLookup(allAgents)),
        [agentData, allAgents]
    );

    useEffect(() => {
        refreshRoster();
    }, [refreshRoster]);

    const fetchData = useCallback(async () => {
        invalidatePrefix(`agentPlayers:${currentUser?.userName ?? ''}`);
        await refetch();
    }, [currentUser?.userName, refetch]);

    const handlePinClick = (subAgent: Users, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedSubAgent(subAgent);
        setNewPassword('');
    };
    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewPassword(e.target.value);
    };

    const clearFormTransaction = () => {
        setFormData({
            createDate: formatAdminFiguresShortDate(),
            agent: '',
            customer: '',
            transactionType: 'deposit',
            description: 'Customer Deposit',
            amount: '',
            createBy: '',
        });
    };

    const handleSave = async (userId: string) => {
        if (newPassword.trim() !== '') {
            try {
                const pin = newPassword.trim().toUpperCase();
                const newData: Partial<Users> = { pin };

                await editUser(userId, newData);
                setSelectedSubAgent(prev => prev ? { ...prev, pin } : null);
                updateAgentInContext(userId, newData);
                if (selectedSubAgent?.userName) {
                    patchVisitedUserCaches(selectedSubAgent.userName, newData);
                }
                fetchData();
                alert('Password updated successfully');
                setNewPassword('');

            } catch (error) {
                alert('Error updating');
            }
        } else {
            alert('Unable to update with empty password');
        }
    };

    const updateBalance = (subAgent: Users, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedSubAgent(subAgent);
        setFormData(prevData => ({
            ...prevData,
            customer: subAgent.userName,
            createBy: currentUser?.userName ?? '',
            agent: currentUser?.userName ?? '',
            description: 'Customer Deposit',
            userType: subAgent.userType

        }));
    };

    const calculateNewBalance = () => {
        if (!selectedSubAgent) return 0;
        const amount = parseFloat(formData.amount || '0');
        return formData.transactionType === 'deposit'
            ? selectedSubAgent.balance + amount
            : selectedSubAgent.balance - amount;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        const sanitizedValue = name === 'amount' ? value.replace(/[^0-9.]/g, '') : value;

        setFormData(prevData => {
            const updatedData = {
                ...prevData,
                [name]: sanitizedValue,
            };
            if (name === 'transactionType') {
                updatedData.description = value === 'Deposit' ? 'Customer Deposit' : 'Customer Withdrawal';
            }
            return updatedData;
        });
    };

    const saveTransaction = async () => {
        try {
            if (!selectedSubAgent) {
                alert("No agent selected.");
                return;
            }
            if (isBlockedBalanceAction(balanceActor, formData.transactionType)) {
                return;
            }
            if (formData.amount.trim() === '' || isNaN(Number(formData.amount))) {
                alert("Please enter a valid amount.");
                return;
            }
            const formDataWithTimestamp: any = {
                ...formData,
                createDate: Timestamp.fromDate(new Date()),
            };

            formDataWithTimestamp.amount = parseFloat(formData.amount);
            await addTransaction(formDataWithTimestamp);

            const updatedBalance =
                formData.transactionType === 'deposit'
                    ? selectedSubAgent.balance + formDataWithTimestamp.amount
                    : selectedSubAgent.balance - formDataWithTimestamp.amount;

            if (!selectedSubAgent?.id) {
                alert("Selected agent ID is missing.");
                return;
            }

            await editUser(selectedSubAgent.id, { balance: updatedBalance });
            setSelectedSubAgent((prev) =>
                prev ? { ...prev, balance: updatedBalance } : null
            );
            if (currentUser?.userName) {
                await logUserActivity(
                    currentUser.userName,
                    `User Transaction Update: ${selectedSubAgent.userName} | Type: ${formData.transactionType} | Amount: ${formDataWithTimestamp.amount}`, userIpDataAdmin
                );
            }

            alert("Transaction added successfully and balance updated.");
            clearFormTransaction();
            fetchData();
            clearFormTransaction();

        } catch (error) {
            console.error("Error processing transaction:", error);
            alert("Error adding transaction or updating balance.");
        }
    };


    const handleAgentClick = (agentId: string) => {
        setExpandedAgentIds((prev) =>
            prev.includes(agentId)
                ? prev.filter((id) => id !== agentId)
                : [...prev, agentId]
        );
    };

    const openBetDetailModal = () => {
        const modalElement = document.getElementById("balanceAgent");

        if (!modalElement) return;

        const existingInstance = Modal.getInstance(modalElement);

        if (existingInstance) {
            existingInstance.show();
        } else {
            const newModal = new Modal(modalElement);
            newModal.show();
        }
    };

    const goToConfirmModal = () => {
        if (isBlockedBalanceAction(balanceActor, formData.transactionType)) {
            return;
        }
        const modal1El = document.getElementById("balanceAgent");
        const modal2El = document.getElementById("balanceConfirmAgent");

        if (!modal1El || !modal2El) return;
        const modal2 = Modal.getInstance(modal2El) || new Modal(modal2El);

        modal1El.style.display = "none";

        modal2.show();
    };

    const handleConfirm = () => {
        saveTransaction();

        const modal1El = document.getElementById("balanceAgent");
        const modal2El = document.getElementById("balanceConfirmAgent");

        if (!modal1El || !modal2El) return;

        const modal2 = Modal.getInstance(modal2El) || new Modal(modal2El);
        const modal1 = Modal.getInstance(modal1El) || new Modal(modal1El);

        modal2.hide();
        modal1.hide();

        modal1El.style.display = "";
    };

    const handleCancelConfirm = () => {
        const modal1El = document.getElementById("balanceAgent");
        const modal2El = document.getElementById("balanceConfirmAgent");

        if (!modal1El || !modal2El) return;

        const modal2 = Modal.getInstance(modal2El) || new Modal(modal2El);
        const modal1 = Modal.getInstance(modal1El) || new Modal(modal1El);

        modal2.hide();
        modal1.hide();

        modal1El.style.display = "";

        clearFormTransaction();
    };


    return (
        <>
            {isLoading ? (
                <div className="loader-container">
                    <FaSyncAlt className="loader-icon-proyect" />
                </div>
            ) : (
                <div>
                    <div className='container-manager container-tablePending agent-table-container'>
                        <div className='header-table-agent '>
                            <div className='column column-agents'>
                                <span>Agents</span>
                            </div>
                            <div className='column space'>
                                <span>Password</span>
                            </div>
                            <div className='column'>
                                <span>Balance</span>
                            </div>
                            <div className='column space'>
                                <span>Last Week</span>
                            </div>
                            <div className='column space'>
                                <span>Last Week</span>
                            </div>
                            <div className='column'>
                                <span>Actions</span>
                            </div>
                        </div>
                    </div>
                    <div className='container-bodyPending2'>
                        <div className='header-table-agent level-1'>
                            <div className='columns agents  agent-name-agen'>
                                <span>{currentUser?.userName}</span>
                            </div>
                            {/* <div className='columns agents  agent-name-agen'>
                                <span>${currentUser?.balance}</span>
                            </div> */}
                        </div>
                        {agent.length > 0 ? (
                            agent.map(subAgent => (
                                <React.Fragment key={subAgent.userName}>
                                    <div className="header-table-agent option-ejem level-2" onClick={() => handleAgentClick(subAgent.userName)}>
                                        <div className='columns column-agents agents'>
                                            <span>{subAgent.userName}</span>
                                        </div>
                                        <div className='columns space' data-bs-toggle="modal" data-bs-target="#agentManag" onClick={(e) => handlePinClick(subAgent, e)}>
                                            <span className='cursor'>{subAgent.pin}</span>
                                        </div>
                                        <div className='columns' onClick={(e) => {
                                            if (!agentCanAdjustBalances(balanceActor)) return;
                                            updateBalance(subAgent, e);
                                        }}>
                                            <span className='cursor linea'>${formatNumbers(subAgent.balance)}</span>
                                        </div>
                                        <div className='columns space'><span>{0}</span></div>
                                        <div className='columns space'><span></span></div>
                                        <div className='columns'>
                                            <Link
                                                to={`/user/${subAgent.userName}`}
                                                state={buildVisitedUserNavState(subAgent)}
                                                className='size-pendig'
                                                onClick={() => {
                                                logUserActivity(currentUser!.userName, `View User Basic: ${subAgent.userName}`);
                                            }}>
                                                <button className='actions action-detail'>
                                                    <IoArrowRedoSharp />
                                                </button>
                                            </Link>
                                        </div>
                                    </div>

                                    {expandedAgentIds.includes(subAgent.userName) && (
                                        <>
                                            {subAgent.playersUserNames.map((player, index) => (
                                                <div key={index} className="header-table-agent option-ejem-level-3 level-3">
                                                    <div className="player-name">
                                                        <span>{player.userName}</span>
                                                    </div>
                                                    <div className="columns"><span>--</span></div>
                                                    <div className="columns space"><span></span></div>
                                                    <div className="columns"><span>--</span></div>
                                                    <div className="columns space"><span></span></div>
                                                    <div className="columns space"><span></span></div>
                                                </div>
                                            ))}
                                        </>
                                    )}
                                </React.Fragment>
                            ))
                        ) : (
                            <p>No Data</p>
                        )}
                    </div>

                </div>
            )}

            <div className="modal fade modal-sm" id="agentManag" tabIndex={-1}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header colorse">
                            <h4 className="modal-title fs-5">
                                <FaUserEdit /> {selectedSubAgent ? `${selectedSubAgent.userName}` : ''}
                            </h4>
                        </div>
                        <div className="modal-body">
                            {selectedSubAgent && (
                                <div>
                                    <label htmlFor="newPassword"><FaLock /> Password:</label>
                                    <input type="text" id="newPassword" value={newPassword} onChange={handlePasswordChange} className="form-control" />
                                </div>
                            )}
                        </div>
                        <div className="modal-footer d-flex justify-content-between">
                            <button type="button" className="btn btn-outline-danger" data-bs-dismiss="modal">Cancel</button>
                            <button type="button" className="btn btn-outline-success" onClick={() => selectedSubAgent && selectedSubAgent.id && handleSave(selectedSubAgent.id)} data-bs-dismiss="modal">Save</button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal fade" id="balanceAgent" tabIndex={-1}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title fs-5">
                                <span>New Transaction {selectedSubAgent ? `${selectedSubAgent.userName}` : ''}</span>
                            </h4>
                        </div>
                        <div className="modal-body">
                            <div className="mb-3">
                                <span className="form-label transactionManager">Transaction</span>
                                <select name="transactionType" className="selectManager" value={formData.transactionType} onChange={handleInputChange}>
                                    {agentCanAdjustBalances(balanceActor) && (
                                        <>
                                            <option value="deposit">Deposits</option>
                                            <option value="withdrawal">Withdrawals</option>
                                        </>
                                    )}
                                </select>
                            </div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="transactionManager2">Date</span>
                                <input type="text" id="transactionDate" className="dateTransaction" readOnly value={formData.createDate} />
                            </div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="transactionManager2">Amount</span>
                                <input type="number" name="amount" className="numberTransaction" value={formData.amount} autoComplete='off'
                                    onChange={handleInputChange} />
                            </div>
                            <div className="mb-3">
                                <span className="transactionManager2">Description</span>
                                <textarea name="description" className="descriptionManager" value={formData.description}
                                    onChange={handleInputChange}
                                ></textarea>
                            </div>
                        </div>
                        <div className="modal-footer d-flex justify-content-between">
                            <button type="button" className="btn btn-outline-danger" data-bs-dismiss="modal" onClick={clearFormTransaction}>Cancel</button>
                            <button type="button" className="btn btn-outline-success" onClick={goToConfirmModal} disabled={!agentCanAdjustBalances(balanceActor)}>Continue</button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal fade" id="balanceConfirmAgent" tabIndex={-1}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title fs-5">
                                <span>Confirm Transaction</span>
                            </h4>
                            <button type="button" className="btn-close" onClick={handleCancelConfirm}></button>
                        </div>
                        <div className="modal-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="transactionManager2">Date</span>
                                <span className="transactionManager2">{formatAdminFiguresShortDate()}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="transactionManager2">Previous Balance</span>
                                <span className="transactionManager2">$ {formatCurrency(selectedSubAgent?.balance)}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="transactionManager2">Deposits</span>
                                <span className="transactionManager2">$ {formatCurrency(parseFloat(formData.amount))}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="transactionManager2">New Balance</span>
                                <span className="transactionManager2">$ {formatCurrency(calculateNewBalance())}</span>
                            </div>
                        </div>
                        <div className="modal-footer d-flex justify-content-between">
                            <button type="button" className="btn btn-outline-danger" onClick={handleCancelConfirm}>Cancel</button>
                            <button type="button" className="btn btn-outline-success" onClick={handleConfirm}>Confirm</button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default AgentManagement;